import React, {
  useRef,
  useState,
  type ComponentRef,
  type CSSProperties,
} from "react";
import { useCreate, useGetIdentity, useList } from "@refinedev/core";
import { useTranslate } from "@refinedev/core";
import {
  Modal,
  Card,
  Flex,
  Typography,
  Spin,
  Empty,
  InputNumber,
  Button,
  Result,
  theme,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { useTenantSettings } from "../../hooks/tenant";
import { ICreditPack } from "../../models/credit-pack";

const DEFAULT_PRICE_PER_CREDIT = 10;
const MODAL_WIDTH = 440;

const getPackPricePerCredit = (pack: ICreditPack): number =>
  pack.credits > 0 ? Math.round(pack.priceExclTax / 100 / pack.credits) : 0;

type IUser = {
  account?: {
    id: number;
    billingMode: string;
    credit: number;
    applyVat?: boolean;
    customVatRate?: number;
  };
};

interface CreditBuyModalProps {
  isOpen: boolean;
  onCancel: () => void;
  accountCredit: number;
}

export const CreditBuyModal: React.FC<CreditBuyModalProps> = ({
  isOpen,
  onCancel,
  accountCredit: _accountCredit,
}) => {
  const { data: user } = useGetIdentity<IUser>();
  const { mutate: create } = useCreate();
  const _t = useTranslate();
  const { token } = theme.useToken();
  const tenantSettings = useTenantSettings();
  const manualInputRef = useRef<ComponentRef<typeof InputNumber>>(null);

  const getCardStyle = (isSelected: boolean): CSSProperties => ({
    borderColor: isSelected ? token.colorPrimary : token.colorBorder,
    boxShadow: isSelected ? `0 0 0 2px ${token.colorPrimary}` : undefined,
  });

  const selectedTextStyle: CSSProperties = { color: token.colorPrimary };

  const pricePerCredit =
    tenantSettings?.pricePerCredit ?? DEFAULT_PRICE_PER_CREDIT;
  const showManualBlock = pricePerCredit > 0;

  const vatRate =
    user?.account?.applyVat && user?.account?.customVatRate
      ? user.account.customVatRate
      : tenantSettings?.defaultVatRate ?? 0;

  const { data: packsData, isLoading: packsLoading } = useList<ICreditPack>({
    resource: "credit-packs",
    filters: [{ field: "isActive", operator: "eq", value: true }],
    pagination: { mode: "off" },
    queryOptions: { enabled: isOpen },
  });

  const packs = packsData?.data ?? [];

  const sortedPacks = [...packs].sort((a, b) => a.credits - b.credits);

  const [selectedPack, setSelectedPack] = useState<ICreditPack | null>(null);
  const [creditToBuy, setCreditToBuy] = useState(0);
  const [paymentInitiated, setPaymentInitiated] = useState(false);

  const isManualSelected = creditToBuy > 0 && !selectedPack;
  const manualPrice = Math.round(creditToBuy * pricePerCredit);

  const handleSelectPack = (pack: ICreditPack) => {
    setSelectedPack(pack);
    setCreditToBuy(0);
  };

  const handleSetManualCredit = (value: number | null) => {
    setCreditToBuy(value ?? 0);
    setSelectedPack(null);
  };

  const handleSelectManualCard = () => {
    setSelectedPack(null);
    manualInputRef.current?.focus();
  };

  const handleBuyModalOk = () => {
    if (!selectedPack && creditToBuy <= 0) return;

    let credit: number;
    let price: number;
    let creditPackId: number | undefined;

    if (selectedPack) {
      credit = selectedPack.credits;
      price = selectedPack.priceExclTax;
      creditPackId = selectedPack.id;
    } else {
      credit = creditToBuy;
      price = Math.round(creditToBuy * pricePerCredit * 100);
    }

    const vatAmount = vatRate > 0 ? Math.round((price * vatRate) / 100) : 0;

    create(
      {
        resource: "payments",
        values: {
          credit,
          price,
          vat: vatAmount,
          ...(creditPackId !== undefined ? { creditPackId } : {}),
          accountId: `${user?.account?.id}`,
        },
      },
      {
        onSuccess: (res) => {
          window.open(res.data?.url, "_blank");
          setPaymentInitiated(true);
        },
      },
    );
  };

  const handleCancel = () => {
    setSelectedPack(null);
    setCreditToBuy(0);
    setPaymentInitiated(false);
    onCancel();
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const hasNoPacks = !packsLoading && packs.length === 0;
  const showEmpty = hasNoPacks && !showManualBlock;
  const showStack = sortedPacks.length > 0 || showManualBlock;

  return (
    <Modal
      title={_t("credits.titles.buy")}
      open={isOpen}
      onOk={handleBuyModalOk}
      onCancel={handleCancel}
      okText={_t("credits.titles.next", "Next")}
      okButtonProps={{ disabled: !selectedPack && creditToBuy <= 0 }}
      footer={paymentInitiated ? null : undefined}
      width={MODAL_WIDTH}
      styles={{ body: { paddingTop: 12, paddingBottom: 8 } }}
    >
      {paymentInitiated ? (
        <Result
          status="info"
          title={_t("credits.titles.completePurchaseTitle")}
          subTitle={_t("credits.titles.completePurchaseMessage")}
          extra={
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
            >
              {_t("credits.titles.refreshPage", "Refresh")}
            </Button>
          }
        />
      ) : packsLoading ? (
        <Flex justify="center" className="py-10">
          <Spin size="large" />
        </Flex>
      ) : (
        <>
          {showStack && (
            <Flex vertical gap={10}>
              {sortedPacks.map((pack) => {
                const isSelected = selectedPack?.id === pack.id;
                return (
                  <Card
                    key={pack.id}
                    size="small"
                    bordered
                    className="w-full cursor-pointer transition-all"
                    style={getCardStyle(isSelected)}
                    hoverable
                    onClick={() => handleSelectPack(pack)}
                  >
                    <Flex justify="space-between" align="center" gap={12}>
                      <Flex vertical gap={2} className="min-w-0 flex-1">
                        <Typography.Text
                          strong
                          style={isSelected ? selectedTextStyle : undefined}
                        >
                          {pack.name}
                        </Typography.Text>
                        <Flex align="baseline" gap={12} wrap="wrap">
                          <Typography.Text className="text-2xl font-semibold leading-none">
                            {pack.credits}
                          </Typography.Text>
                          <Typography.Text
                            type="secondary"
                            className="text-sm"
                          >
                            {_t("credits.credits")}
                          </Typography.Text>
                        </Flex>
                        <Typography.Text type="secondary" className="text-xs">
                          1 {_t("credits.credit")} ={" "}
                          {getPackPricePerCredit(pack)} €
                        </Typography.Text>
                        {pack.description && (
                          <Typography.Text type="secondary" className="text-xs">
                            {pack.description}
                          </Typography.Text>
                        )}
                      </Flex>
                      <Typography.Text className="shrink-0 text-2xl font-semibold whitespace-nowrap">
                        {Math.round(pack.priceExclTax / 100)} €{" "}
                        <Typography.Text
                          type="secondary"
                          className="text-xs font-normal"
                        >
                          {_t("credit-packs.fields.priceExclTaxShort", "HT")}
                        </Typography.Text>
                      </Typography.Text>
                    </Flex>
                  </Card>
                );
              })}

              {showManualBlock && (
                <Card
                  size="small"
                  bordered
                  className="w-full cursor-pointer transition-all"
                  style={getCardStyle(isManualSelected)}
                  hoverable
                  onClick={handleSelectManualCard}
                >
                  <Flex justify="space-between" align="center" gap={12}>
                    <Flex vertical gap={4} className="min-w-0 flex-1">
                      <Typography.Text type="secondary" className="text-xs">
                        {_t("credits.titles.customAmount")}
                      </Typography.Text>
                      <Flex align="baseline" gap={8} wrap="wrap" onClick={(e) => e.stopPropagation()}>
                        <InputNumber
                          ref={manualInputRef}
                          value={creditToBuy || undefined}
                          onChange={handleSetManualCredit}
                          size="large"
                          className="w-full max-w-[80px] [&_input]:!text-2xl"
                          min={1}
                          precision={0}
                          changeOnBlur={false}
                          placeholder="0"
                        />
                        <Typography.Text type="secondary" className="text-sm">
                          {_t("credits.credits")}
                        </Typography.Text>
                      </Flex>
                      <Typography.Text type="secondary" className="text-xs">
                        1 {_t("credits.credit")} = {Math.round(pricePerCredit)}{" "}
                        €
                      </Typography.Text>
                    </Flex>
                    <Typography.Text
                      className="shrink-0 text-2xl font-semibold whitespace-nowrap"
                      style={isManualSelected ? selectedTextStyle : undefined}
                    >
                      {manualPrice} €{" "}
                      <Typography.Text
                        type="secondary"
                        className="text-xs font-normal"
                      >
                        {_t("credit-packs.fields.priceExclTaxShort", "HT")}
                      </Typography.Text>
                    </Typography.Text>
                  </Flex>
                </Card>
              )}
            </Flex>
          )}

          {showEmpty && (
            <Flex justify="center" className="py-10">
              <Empty
                description={_t(
                  "credit-packs.messages.noPacks",
                  "No credit packs available",
                )}
              />
            </Flex>
          )}
        </>
      )}

      {vatRate > 0 && !paymentInitiated && (
        <Flex justify="center" className="mt-2">
          <Typography.Text className="text-xs" type="secondary">
            {_t("credits.titles.vatNotice")}
          </Typography.Text>
        </Flex>
      )}
    </Modal>
  );
};
